import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createAction,
  getActionById,
  listActionsBySubscription,
  getAvailableUpgrades,
  type ServerAction,
} from '../api/actions';

export function useAvailableUpgrades(subscriptionId: string) {
  return useQuery({
    queryKey: ['available-upgrades', subscriptionId],
    queryFn: () => getAvailableUpgrades(subscriptionId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAction(actionId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['action', actionId],
    queryFn: () => getActionById(actionId!),
    enabled: !!actionId && (options?.enabled !== false),
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 5 seconds while pending/running
      if (data?.status === 'pending' || data?.status === 'claimed' || data?.status === 'running') {
        return 5000;
      }
      return false;
    },
  });
}

export function useSubscriptionActions(subscriptionId: string) {
  return useQuery({
    queryKey: ['actions', subscriptionId],
    queryFn: () => listActionsBySubscription(subscriptionId),
  });
}

export function useCreateAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAction,
    onSuccess: (data) => {
      if (data.subscription_id) {
        queryClient.invalidateQueries({ queryKey: ['actions', data.subscription_id] });
      }
    },
  });
}

export function useDeviceSearch(subscriptionId: string) {
  const createActionMutation = useCreateAction();

  const search = async (macAddresses: string[]): Promise<ServerAction> => {
    const action = await createActionMutation.mutateAsync({
      subscription_id: subscriptionId,
      action_type: 'device_search',
      payload: { mac_addresses: macAddresses },
    });
    return action;
  };

  return { search, isPending: createActionMutation.isPending, error: createActionMutation.error };
}

export function useDeviceDelete(subscriptionId: string) {
  const createActionMutation = useCreateAction();

  const deleteDevices = async (macAddresses: string[]): Promise<ServerAction> => {
    const action = await createActionMutation.mutateAsync({
      subscription_id: subscriptionId,
      action_type: 'device_delete',
      payload: { mac_addresses: macAddresses },
    });
    return action;
  };

  return { deleteDevices, isPending: createActionMutation.isPending, error: createActionMutation.error };
}
